import "@/app/utils/sentry";
import "@fontsource-variable/geist";
import "@fontsource-variable/geist-mono";
import { ChakraProvider, ColorModeScript } from "@chakra-ui/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { MotionConfig } from "framer-motion";
import React from "react";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { createRoot } from "react-dom/client";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { BrowserRouter } from "react-router";

import AppRoutes from "@/app/components/Routes";
import { bootstrapLoadingSideEffects } from "@/auth/data/useBootstrapData";
import ErrorBoundary from "@/common/components/ErrorBoundary";
import { UnsavedChangesProvider } from "@/common/contexts/UnsavedChangesContext";
import { commonQueryKeys } from "@/common/data/commonQueryKeys";
import { theme } from "@/theme/theme";
import { BootstrapDTO } from "@reflag/shared/bootstrapAPI";

declare global {
  interface Window {
    bootstrapData?: BootstrapDTO;
  }
}

const container = document.getElementById("root");
const root = createRoot(container!); // createRoot(container!) if you use TypeScript

const queryClient = new QueryClient({});

// Set the bootstrap data in the query cache if it exists
if (window.bootstrapData) {
  queryClient.setQueryData(
    commonQueryKeys.bootstrap(window.bootstrapData?.org?.id),
    window.bootstrapData,
  );
  bootstrapLoadingSideEffects(
    window.bootstrapData,
    queryClient,
    "server-bootstrap",
  );
}

const toastOptions = {
  defaultOptions: {
    duration: 2500,
    isClosable: false,
    variant: "neutral",
    containerStyle: {
      margin: 1,
      minW: 0,
    },
  },
};

root.render(
  <>
    <ColorModeScript initialColorMode={theme.config.initialColorMode} />
    <React.StrictMode>
      <HelmetProvider>
        <Helmet defaultTitle="Reflag"></Helmet>
        <ChakraProvider theme={theme} toastOptions={toastOptions}>
          <MotionConfig transition={{ duration: 0.1 }}>
            <QueryClientProvider client={queryClient}>
              <UnsavedChangesProvider>
                <BrowserRouter>
                  <DndProvider backend={HTML5Backend}>
                    <ErrorBoundary>
                      <AppRoutes />
                    </ErrorBoundary>
                  </DndProvider>
                </BrowserRouter>
              </UnsavedChangesProvider>
              <ReactQueryDevtools
                buttonPosition="bottom-right"
                initialIsOpen={false}
                position="bottom"
              />
            </QueryClientProvider>
          </MotionConfig>
        </ChakraProvider>
      </HelmetProvider>
    </React.StrictMode>
  </>,
);
